import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useCreateStudent, useUpdateStudent } from '../../lib/hooks';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from '../ui/sheet';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../lib/translations';

const createSchema = z.object({
  first_name: z.string().min(1, 'Le prénom est requis'),
  last_name: z.string().min(1, 'Le nom est requis'),
  email: z.string().email('Email invalide'),
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  phone: z.string().min(1, 'Le téléphone est requis'),
  whatsapp: z.string().optional(),
  nationality: z.string().min(1, 'La nationalité est requise'),
  date_of_birth: z.string().min(1, 'La date de naissance est requise'),
  address: z.string().optional(),
  german_level: z.string().optional(),
  assigned_level: z.string().optional(),
  visa_objective: z.string().optional(),
  notes: z.string().optional(),
});

const updateSchema = z.object({
  first_name: z.string().min(1, 'Le prénom est requis'),
  last_name: z.string().min(1, 'Le nom est requis'),
  email: z.string().email('Email invalide'),
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères').optional().or(z.literal('')),
  phone: z.string().min(1, 'Le téléphone est requis'),
  whatsapp: z.string().optional(),
  nationality: z.string().min(1, 'La nationalité est requise'),
  date_of_birth: z.string().min(1, 'La date de naissance est requise'),
  address: z.string().optional(),
  german_level: z.string().optional(),
  assigned_level: z.string().optional(),
  visa_objective: z.string().optional(),
  notes: z.string().optional(),
});

const StudentForm = ({ open, onClose, student }) => {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const isEdit = !!student;
  const [showPasswordField, setShowPasswordField] = useState(!isEdit);
  
  // Role-based field restrictions
  const isAdmin = user?.role === 'admin';
  const isRespPeda = user?.role === 'resp_peda';
  const isRespAdmin = user?.role === 'resp_admin';
  
  // resp_peda cannot edit visa fields, resp_admin cannot edit course fields
  // Admin can edit all, resp_peda can edit course fields only, resp_admin can edit visa fields only
  const canEditVisaFields = isAdmin || isRespAdmin; // resp_peda CANNOT edit visa fields
  const canEditCourseFields = isAdmin || isRespPeda; // resp_admin CANNOT edit course fields
  const canCreate = isAdmin; // Only admin can create
  const canEdit = isAdmin || isRespPeda || isRespAdmin; // All three can edit (with restrictions)
  
  const createStudent = useCreateStudent();
  const updateStudent = useUpdateStudent(student?.id);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(isEdit ? updateSchema : createSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: '',
      whatsapp: '',
      nationality: '',
      date_of_birth: '',
      address: '',
      german_level: 'A1',
      assigned_level: '',
      visa_objective: 'studies',
      notes: '',
    },
  });

  useEffect(() => {
    if (student) {
      reset({
        first_name: student.first_name || '',
        last_name: student.last_name || '',
        email: student.email || '',
        password: '',
        phone: student.phone || '',
        whatsapp: student.whatsapp || '',
        nationality: student.nationality || '',
        date_of_birth: student.date_of_birth || '',
        address: student.address || '',
        german_level: student.german_level || 'A1',
        assigned_level: student.assigned_level || '',
        visa_objective: student.visa_objective || 'studies',
        notes: student.notes || '',
      });
      setShowPasswordField(false);
    } else {
      reset({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        phone: '',
        whatsapp: '',
        nationality: '',
        date_of_birth: '',
        address: '',
        german_level: 'A1',
        assigned_level: '',
        visa_objective: 'studies',
        notes: '',
      });
      setShowPasswordField(true);
    }
  }, [student, reset]);

  const onSubmit = async (data) => {
    try {
      // Remove empty password for updates
      if (isEdit && !data.password) {
        delete data.password;
      }
      
      if (isEdit) {
        await updateStudent.mutateAsync(data);
        toast.success('Étudiant modifié avec succès');
      } else {
        await createStudent.mutateAsync(data);
        toast.success('Étudiant créé avec succès');
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'opération');
    }
  };

  const germanLevel = watch('german_level');
  const visaObjective = watch('visa_objective');
  const assignedLevel = watch('assigned_level');

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading text-xl">
            {isEdit ? 'Modifier l\'étudiant' : 'Nouvel étudiant'}
          </SheetTitle>
          <SheetDescription>
            {isEdit 
              ? 'Modifiez les informations de l\'étudiant' 
              : 'Remplissez les informations du nouvel étudiant'
            }
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-6">
          {/* Section Compte */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Compte</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Prénom *</Label>
                <Input
                  id="first_name"
                  {...register('first_name')}
                  className={errors.first_name ? 'border-destructive' : ''}
                  data-testid="student-firstname-input"
                />
                {errors.first_name && (
                  <p className="text-xs text-destructive">{errors.first_name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Nom *</Label>
                <Input
                  id="last_name"
                  {...register('last_name')}
                  className={errors.last_name ? 'border-destructive' : ''}
                  data-testid="student-lastname-input"
                />
                {errors.last_name && (
                  <p className="text-xs text-destructive">{errors.last_name.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                className={errors.email ? 'border-destructive' : ''}
                data-testid="student-email-input"
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>

            {isEdit ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t('settings_password', lang)}</Label>
                  <Switch
                    checked={showPasswordField}
                    onCheckedChange={setShowPasswordField}
                  />
                </div>
                {showPasswordField && (
                  <Input
                    type="password"
                    placeholder="Nouveau mot de passe (min 8 caractères)"
                    {...register('password')}
                    className={errors.password ? 'border-destructive' : ''}
                    data-testid="student-password-input"
                  />
                )}
                {errors.password && showPasswordField && (
                  <p className="text-xs text-destructive">{errors.password.message}</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe *</Label>
                <Input
                  id="password"
                  type="password"
                  {...register('password')}
                  className={errors.password ? 'border-destructive' : ''}
                  data-testid="student-password-input"
                />
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password.message}</p>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Section Profil */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Profil</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">{lang === 'de' ? 'Telefon *' : 'Téléphone *'}</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                  placeholder="+33 6 12 34 56 78"
                  data-testid="student-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp">WhatsApp</Label>
                <Input
                  id="whatsapp"
                  {...register('whatsapp')}
                  placeholder="+33 6 12 34 56 78"
                  data-testid="student-whatsapp-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nationality">{lang === 'de' ? 'Nationalität *' : 'Nationalité *'}</Label>
                <Input
                  id="nationality"
                  {...register('nationality')}
                  data-testid="student-nationality-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_of_birth">{lang === 'de' ? 'Geburtsdatum *' : 'Date de naissance *'}</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  {...register('date_of_birth')}
                  data-testid="student-dob-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">{t('students_address', lang)}</Label>
              <Textarea
                id="address"
                {...register('address')}
                rows={2}
                data-testid="student-address-input"
              />
            </div>
          </div>

          <Separator />

          {/* Section Formation */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Formation</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{lang === 'de' ? 'Aktuelles Niveau' : 'Niveau actuel'}</Label>
                <Select
                  value={germanLevel}
                  onValueChange={(value) => setValue('german_level', value)}
                >
                  <SelectTrigger data-testid="student-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debutant">Débutant</SelectItem>
                    <SelectItem value="A1">A1</SelectItem>
                    <SelectItem value="A2">A2</SelectItem>
                    <SelectItem value="B1">B1</SelectItem>
                    <SelectItem value="B2">B2</SelectItem>
                    <SelectItem value="C1">C1</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{lang === 'de' ? 'Zugewiesenes Niveau' : 'Niveau assigné'}</Label>
                <Select
                  value={assignedLevel || ''}
                  onValueChange={(value) => setValue('assigned_level', value)}
                >
                  <SelectTrigger data-testid="student-assigned-level-select">
                    <SelectValue placeholder="Non assigné" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{lang === 'de' ? 'Nicht zugewiesen' : 'Non assigné'}</SelectItem>
                    <SelectItem value="A1">A1</SelectItem>
                    <SelectItem value="A2">A2</SelectItem>
                    <SelectItem value="B1">B1</SelectItem>
                    <SelectItem value="B2">B2</SelectItem>
                    <SelectItem value="C1">C1</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('dossier_detail_visa', lang)}</Label>
              <Select
                value={visaObjective}
                onValueChange={(value) => setValue('visa_objective', value)}
              >
                <SelectTrigger data-testid="student-visa-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="studies">{lang === 'de' ? 'Studium' : 'Études'}</SelectItem>
                  <SelectItem value="professional">{lang === 'de' ? 'Berufsausbildung' : 'Formation professionnelle'}</SelectItem>
                  <SelectItem value="work">{lang === 'de' ? 'Arbeit' : 'Travail'}</SelectItem>
                  <SelectItem value="family">{lang === 'de' ? 'Familienzusammenführung' : 'Regroupement familial'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Section Notes */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Notes internes</h3>
            <Textarea
              {...register('notes')}
              rows={4}
              placeholder="Notes visibles uniquement par l'administration..."
              data-testid="student-notes-input"
            />
          </div>

          <SheetFooter className="gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting} data-testid="student-submit-btn">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                isEdit ? 'Enregistrer les modifications' : 'Créer l\'étudiant'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default StudentForm;
